import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {
  sendFeedbackToSlack,
  type FeedbackFormData,
} from '@pflegenavi/shared-frontend/platform';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';

// Global reference to auth context for Sentry feedback
// This is set by the AuthenticationProvider after initialization
let globalAuthContext: AuthenticationContext | undefined;
let globalApiUrl: string | undefined;

// Track processed feedback events to prevent duplicates
const processedFeedbackEvents = new Set<string>();

export function setSentryAuthContext(authContext: AuthenticationContext): void {
  globalAuthContext = authContext;
}

export function setSentryApiUrl(apiUrl: string): void {
  globalApiUrl = apiUrl;
}

const getEnvironment = () => {
  if (window.location.hostname.includes('localhost')) {
    return 'local';
  }
  return window.location.hostname.includes('staging.pflegenavi.at')
    ? 'staging'
    : 'production';
};

const getTracePropagationTargets = () => {
  if (getEnvironment() === 'local') {
    return ['localhost:3005', 'localhost:3006', 'localhost:4000'];
  } else if (getEnvironment() === 'staging') {
    return ['api.staging.pflegenavi.at', 'phoenix.staging.pflegenavi.at'];
  } else {
    return ['api.pflegenavi.at', 'phoenix.pflegenavi.at'];
  }
};

const additionalPlugins = () => {
  // Test it on staging first
  if (getEnvironment() !== 'production') {
    const apiUrl =
      getEnvironment() === 'staging'
        ? 'api.staging.pflegenavi.at'
        : 'localhost:3005';
    return [
      Sentry.replayIntegration({
        // Additional SDK configuration goes in here, for example:
        maskAllInputs: true,
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [apiUrl],
      }),
      Sentry.browserProfilingIntegration(),
      // User Feedback Widget - enabled in staging/local only
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        autoInject: true,
        showBranding: false,
        isEmailRequired: false,
        isNameRequired: false,
        emailLabel: 'Email (optional)',
        nameLabel: 'Name (optional)',
        buttonLabel: 'Report Bug',
        formTitle: 'Report an Issue',
        messagePlaceholder: 'Describe what happened...',
        onSubmitSuccess: (data: FeedbackFormData, eventID: string) => {
          if (processedFeedbackEvents.has(eventID)) {
            return;
          }

          processedFeedbackEvents.add(eventID);

          // Fire and forget - v10 provides both data and eventID
          void (async () => {
            if (!globalAuthContext) {
              // eslint-disable-next-line no-console
              console.warn(
                'Auth context not available for feedback submission'
              );
              return;
            }
            await sendFeedbackToSlack({
              authContext: globalAuthContext,
              feedbackData: data,
              sentryEventId: eventID,
              apiUrl: globalApiUrl,
              appName: 'nursing-home-web',
            });
          })();
        },
        onSubmitError: (error: Error) => {
          // eslint-disable-next-line no-console
          console.error('Feedback submission failed:', error);
        },
      }),
    ];
  }

  return [
    Sentry.replayIntegration({
      maskAllInputs: true,
      maskAllText: true,
      blockAllMedia: true,
      networkDetailAllowUrls: ['pflegenavi.at'],
      mutationLimit: 1000,
      mutationBreadcrumbLimit: 250,
    }),
  ];
};

const sampleRates = () => {
  const environment = getEnvironment();
  if (environment === 'local') {
    return {
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      sampleRate: 1.0,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: 1.0,
      profilesSampleRate: 1.0,
    };
  } else if (environment === 'staging') {
    return {
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sampleRate: 1.0,
      tracesSampleRate: 0.1,
      // Profiles sample rate is relative to tracesSampleRate
      profilesSampleRate: 1.0,
    };
  } else {
    return {
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
      sampleRate: 1.0,
      tracesSampleRate: 0.1,
      // Profiles sample rate is relative to tracesSampleRate
      profilesSampleRate: 1.0,
    };
  }
};

Sentry.init({
  dsn: 'https://92df199ec69343bd987bc95784b88510@o1342624.ingest.sentry.io/6619093',
  enabled: import.meta.env.PROD,
  environment: getEnvironment(),
  integrations: [
    ...additionalPlugins(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracePropagationTargets: getTracePropagationTargets(),
  ...sampleRates(),
});

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
